import moment from 'moment';
import { StationInsight } from '@/types/insights.types';
import { toCurrencySymbol } from '@/utils/currency.utils';

// Raw insight data from API (has 'id' property)
interface RawInsight {
  id: string;
  name: string;
  description: string;
  deviceId?: string;
  monetizedAnnualValue?: number;
  currency?: string;
  valueType?: string;
  status?: string;
  dateCreated?: string;
  isInternal?: boolean;
}

interface StationStore {
  currentStation?: { name?: string };
  assetsDevices?: Array<{ deviceId?: string; SfsContract?: string }>;
}

interface DevicesStore {
  getDeviceById: (deviceId: string) => { name: string } | undefined;
}

export const convertInsightToStationInsight = (
  insight: RawInsight,
  stationStore: StationStore,
  devicesStore: DevicesStore,
  t: (key: string) => string,
): StationInsight => {
  const currencySymbol = toCurrencySymbol(insight.currency);
  const value =
    insight.monetizedAnnualValue && insight.currency
      ? `${currencySymbol}${insight.monetizedAnnualValue.toLocaleString()}`
      : '';

  const typeMap: Record<string, 'energy' | 'risk' | 'maintenance' | 'other'> = {
    energy: 'energy',
    risk: 'risk',
    maintenance: 'maintenance',
    other: 'other',
  };

  const statusLabels: Record<string, string> = {
    draft: t('InsightStatusNew'),
    open: t('InsightStatusPublished'),
    archived: t('InsightStatusArchived'),
  };

  const typeLabels: Record<string, string> = {
    energy: t('InsightTypeEnergy'),
    risk: t('InsightTypeRisk'),
    maintenance: t('InsightTypeMaintenance'),
    other: t('InsightTypeOther'),
  };

  let assetName: string | undefined;
  if (insight.deviceId && devicesStore) {
    const device = devicesStore.getDeviceById(insight.deviceId);
    assetName = device?.name;
  }

  const device =
    assetName && assetName !== 'system' && assetName !== stationStore?.currentStation?.name
      ? assetName
      : t('StationLevel');

  const isInternal = Boolean(insight.isInternal);
  const insightSource: 'internal' | 'manual' = isInternal ? 'internal' : 'manual';
  const insightSourceLabel = isInternal ? t('IOTInsight') : t('ManualInsight');

  let servicePackageIndicator: string | undefined;
  if (insight.deviceId && stationStore?.assetsDevices) {
    const asset = stationStore.assetsDevices.find(a => a.deviceId === insight.deviceId);
    if (asset?.SfsContract) {
      servicePackageIndicator = asset.SfsContract;
    }
  }

  const formattedDate = insight.dateCreated ? moment(insight.dateCreated).format('ll') : '';

  return {
    id: insight.id,
    type: typeMap[insight.valueType?.toLowerCase() || ''] || 'other',
    name: insight.name,
    description: insight.description,
    device,
    value,
    date: formattedDate,
    status: insight.status?.toLowerCase() || 'draft',
    statusLabel: statusLabels[insight.status?.toLowerCase() || ''] || t('InsightStatusNew'),
    typeLabel: typeLabels[insight.valueType?.toLowerCase() || ''] || t('InsightTypeOther'),
    servicePackageIndicator,
    insightSource,
    insightSourceLabel,
    deviceId: insight.deviceId || undefined,
  };
};
